import { billingAccountsApi, subscriptionBillingAccountApi } from "client/api-v2";
import type {
  BillingAccountDetailDto,
  BillingAccountDto,
  BillingAccountTypeFilter,
  ContactDto,
  CreateBillingAccountDto,
  EntityHistoryReferenceDto,
  IntegrationAttemptDto,
  PushBillingAccountToBillingSystemResultDto,
  SaveContactDto,
  SubscriptionTransferPreviewDto,
  UpdateBillingAccountDto,
  UpdateBillingAccountResultDto,
} from "client/api-v2";
import axiosClient from "client/axios-client";

export async function getBillingAccounts(
  params: {
    search?: string;
    type?: BillingAccountTypeFilter;
    page?: number;
    pageSize?: number;
    sortFieldName?: string;
    sortDirection?: "Asc" | "Desc";
  },
  signal: AbortSignal
): Promise<{ items: BillingAccountDto[]; totalItems: number }> {
  const { data } = await billingAccountsApi.getV2BillingAccounts({
    query: {
      search: params.search,
      type: params.type,
      page: params.page || 1,
      pageSize: params.pageSize || 25,
      sortFieldName: params.sortFieldName,
      sortDirection: params.sortDirection,
    },
    signal,
  });

  return {
    items: data.items || [],
    totalItems: data.totalItems || 0,
  };
}

export async function getBillingAccountByCode(billingAccountCode: string): Promise<BillingAccountDetailDto> {
  const { data } = await billingAccountsApi.getV2BillingAccountsByCode({ path: { code: billingAccountCode } });
  return data;
}

export async function addBillingAccountContact(
  billingAccountCode: string,
  contact: SaveContactDto
): Promise<ContactDto> {
  const { data } = await billingAccountsApi.postV2BillingAccountsByCodeContacts({
    path: { code: billingAccountCode },
    body: contact,
  });
  return data;
}

export async function updateBillingAccountContact(
  billingAccountCode: string,
  contactId: string,
  contact: SaveContactDto
): Promise<ContactDto> {
  const { data } = await billingAccountsApi.putV2BillingAccountsByCodeContactsByContactId({
    path: { code: billingAccountCode, contactId },
    body: contact,
  });
  return data;
}

export async function removeBillingAccountContact(billingAccountCode: string, contactId: string): Promise<void> {
  await billingAccountsApi.deleteV2BillingAccountsByCodeContactsByContactId({
    path: { code: billingAccountCode, contactId },
  });
}

export async function pushBillingAccountToBillingSystem(
  billingAccountCode: string,
  reason: string
): Promise<PushBillingAccountToBillingSystemResultDto> {
  const { data } = await billingAccountsApi.postV2BillingAccountsByCodePushToBillingSystem({
    path: { code: billingAccountCode },
    body: { reason },
  });
  return data;
}

export async function removeSubscriptionFromBillingAccount(subscriptionId: string): Promise<void> {
  await subscriptionBillingAccountApi.deleteV2SubscriptionsBySubscriptionIdBillingAccount({
    path: { subscriptionId },
  });
}

export async function createBillingAccount(dto: CreateBillingAccountDto): Promise<BillingAccountDto> {
  const { data } = await billingAccountsApi.postV2BillingAccounts({ body: dto });
  return data;
}

export async function assignSubscriptionToBillingAccount(
  subscriptionId: string,
  billingAccountCode: string
): Promise<void> {
  await subscriptionBillingAccountApi.postV2SubscriptionsBySubscriptionIdBillingAccount({
    path: { subscriptionId },
    body: { billingAccountCode },
  });
}

export async function getSubscriptionTransferPreview(subscriptionId: string): Promise<SubscriptionTransferPreviewDto> {
  const { data } = await subscriptionBillingAccountApi.getV2SubscriptionsBySubscriptionIdBillingAccountTransferPreview({
    path: { subscriptionId },
  });
  return data;
}

export async function updateBillingAccount(
  billingAccountCode: string,
  dto: UpdateBillingAccountDto
): Promise<UpdateBillingAccountResultDto> {
  const { data } = await billingAccountsApi.putV2BillingAccountsByCode({
    path: { code: billingAccountCode },
    body: dto,
  });
  return data;
}

export async function getBillingAccountIntegrationAttempts(
  billingAccountCode: string | undefined
): Promise<IntegrationAttemptDto[]> {
  if (!billingAccountCode) {
    return [];
  }

  const { data } = await billingAccountsApi.getV2BillingAccountsByCodeIntegrationAttempts({
    path: { code: billingAccountCode },
  });
  return data;
}

export async function getBillingAccountHistory(billingAccountCode: string): Promise<EntityHistoryReferenceDto[]> {
  const { data } = await billingAccountsApi.getV2BillingAccountsByCodeHistory({ path: { code: billingAccountCode } });
  return data;
}

export async function getBillingAccountHistorySnapshot(
  billingAccountCode: string,
  asOf: string
): Promise<Record<string, unknown>> {
  const response = await axiosClient.get<Record<string, unknown>>(
    `/v2/billing-accounts/${billingAccountCode}/history-snapshot`,
    { params: { asOf } }
  );
  return response.data;
}
